import type { ToastMessageOptions } from 'primevue/toast';
import { t } from 'i18next';
import { defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';

import type { IApiErrorField } from '@modules/core/services/query/types';
import {
  ServerAccessDeniedError,
  ServerGatewayTimeoutError,
  ServerInternalError,
  ServerNotFoundError,
  ServerPayloadSizeError,
  ServerUnavailableError,
} from '@modules/core/errors';
import getValidationErrorMessages from '@modules/core/utils/getValidationErrorMessages';
import prepareTranslationValues from '@modules/core/utils/prepareTranslationValues';
import { isObject } from '@utils/guards';

import type { IToastMessage, ToastPresetType, ToastSeverity } from '../types';
import { RETRY_QUERY_TOAST_GROUP, TOAST_PRESETS } from '../constants';

const useToastStore = defineStore('toast', () => {
  const toast = useToast();
  const presetTranslationPath = 'common.toasts';
  const defaultLife = 5000;
  const defaultConfig: ToastMessageOptions = {
    life: defaultLife,
  };

  const displayToast = (toastMessage: IToastMessage, type: ToastSeverity = 'info', life = defaultLife, group?: ToastMessageOptions['group']): void => {
    toast.add({
      ...defaultConfig,
      life,
      summary: toastMessage.title,
      detail: toastMessage.text,
      severity: type,
      group,
    });
  };

  const displayPresetToast = (presetType: ToastPresetType, text = '', life = defaultLife, group?: ToastMessageOptions['group']): void => {
    const preset = TOAST_PRESETS[presetType];
    toast.add({
      ...defaultConfig,
      life,
      summary: t(`${presetTranslationPath}.${preset.type}`),
      detail: text,
      severity: preset.severity,
      group,
    });
  };

  function showErrorsWithToast(errors: unknown, showInOneToast = true): void {
    let messages: string[] = [];
    let translateValues: Record<string, IApiErrorField['values']> = {};
    const isSpecialCase = errors instanceof ServerAccessDeniedError
      || errors instanceof ServerPayloadSizeError
      || errors instanceof ServerInternalError
      || errors instanceof ServerNotFoundError
      || errors instanceof ServerGatewayTimeoutError
      || errors instanceof ServerUnavailableError;

    if (isSpecialCase) {
      return;
    }

    if (isObject(errors) && isObject(errors.response) && ('errors' in errors.response || 'base' in errors.response) && !isSpecialCase) {
      messages = getValidationErrorMessages(errors.response.errors || errors.response.base);
      translateValues = prepareTranslationValues(errors.response.errors || errors.response.base);
    }

    if (!messages.length) {
      displayPresetToast('error');
      return;
    }

    if (showInOneToast) {
      const life = messages.length > 2 ? defaultLife + (messages.length - 2) * 2000 : defaultLife;
      const fullMessage = messages.map((errorMessage) => t(errorMessage, translateValues[errorMessage])).join('\n\n');

      displayPresetToast('error', fullMessage, life);
    } else {
      for (const errorMessage of messages) {
        displayPresetToast('error', t(errorMessage, translateValues[errorMessage]));
      }
    }
  }

  function retryToast(failureCount: number, maxRetryCount: number): void {
    displayToast(
      { text: t(`${presetTranslationPath}.requests_to_api.retry.request_is_retried`, { failureCount, maxRetryCount }) },
      'warn',
      undefined,
      RETRY_QUERY_TOAST_GROUP,
    );
  }

  function failureRetryToast(): void {
    displayToast(
      { text: t(`${presetTranslationPath}.requests_to_api.retry.request_failed`) },
      'error',
      undefined,
      RETRY_QUERY_TOAST_GROUP,
    );
  }

  function succeedRetryToast(): void {
    displayToast(
      { text: t(`${presetTranslationPath}.requests_to_api.retry.request_succeeded`) },
      'success',
      undefined,
      RETRY_QUERY_TOAST_GROUP,
    );
  }

  return {
    displayToast,
    displayPresetToast,
    showErrorsWithToast,
    retryToast,
    failureRetryToast,
    succeedRetryToast,
  };
});

export default useToastStore;
